/**
 * Plays a product card's video slide (muted, no controls) when it becomes
 * the active slide in the card's swipeable gallery, and pauses it again
 * when the shopper swipes away. Video slides otherwise sit inert - this is
 * what actually starts/stops them.
 *
 * The video itself lives inside a <template> until first activated, so it
 * can't be found by querying for a <video> element - the slide carries a
 * product-media-container--video(/--external_video) class instead, so that's
 * what's checked here.
 *
 * Which slide was previously active is tracked by element reference (not
 * index): jumping more than one slide makes the slideshow briefly reorder
 * slides in the DOM to animate a short scroll, which would make an
 * index-based lookup of "the previous slide" unreliable.
 *
 * @type {WeakMap<Element, HTMLElement>}
 */
const playingVideoBySlideshow = new WeakMap();

/**
 * @param {Element | null | undefined} slide - A slideshow-slide element
 * @returns {(HTMLElement & { showDeferredMedia?: () => void, playMedia?: () => void, pauseMedia?: () => void }) | null}
 */
function getSlideVideo(slide) {
  if (!slide) return null;
  const isVideo =
    slide.classList.contains('product-media-container--video') ||
    slide.classList.contains('product-media-container--external_video');
  if (!isVideo) return null;
  return /** @type {any} */ (slide.querySelector('deferred-media'));
}

document.addEventListener('slideshow:select', (/** @type {Event} */ event) => {
  const slideshow = event.target;
  if (!(slideshow instanceof Element) || !slideshow.closest('.card-gallery')) return;

  const detail = /** @type {any} */ (event).detail;
  if (!detail) return;

  const previousVideo = playingVideoBySlideshow.get(slideshow);
  const currentVideo = getSlideVideo(detail.slide);

  if (previousVideo && previousVideo !== currentVideo) {
    previousVideo.pauseMedia?.();
  }

  if (currentVideo) {
    if (currentVideo.hasAttribute('data-media-loaded')) {
      currentVideo.playMedia?.();
    } else {
      currentVideo.showDeferredMedia?.();
    }
    playingVideoBySlideshow.set(slideshow, currentVideo);
  } else {
    playingVideoBySlideshow.delete(slideshow);
  }
});

// Cards whose video happens to be the initially active slide (e.g. it's the
// featured media) won't get a slideshow:select event on load, so start those too.
document.querySelectorAll(".card-gallery slideshow-slide[aria-hidden='false']").forEach((slide) => {
  const video = getSlideVideo(slide);
  if (!video) return;
  video.showDeferredMedia?.();
  const slideshow = slide.closest('slideshow-component');
  if (slideshow) playingVideoBySlideshow.set(slideshow, video);
});
